import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Flex, Text, Image, Button } from "@chakra-ui/react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import EventOfferTicket from "./EventOfferTicket";
import { getPriceRange } from "../../shared-utils/utils";
const DEFAULTS = {
    primary: "#A56022",
    secondary: "#7A3E0D",
    bg: "#7A3E0D",
    text: "#FFFFFF",
    card_bg: "#7A3E0D",
    card_border: "#5A2E0D",
};
const extractPasses = (event) => {
    const out = [];
    const pushFromSlot = (slot) => {
        if (!slot)
            return;
        if (slot.has_pass_groups && Array.isArray(slot.pass_groups)) {
            slot.pass_groups.forEach((pg) => {
                if (Array.isArray(pg.passes))
                    out.push(...pg.passes);
            });
        }
        if (Array.isArray(slot.direct_passes))
            out.push(...slot.direct_passes);
        if (Array.isArray(slot.pass_details))
            out.push(...slot.pass_details);
    };
    if ((event === null || event === void 0 ? void 0 : event.has_sub_events) && Array.isArray(event === null || event === void 0 ? void 0 : event.sub_events)) {
        event.sub_events.forEach((se) => {
            if (Array.isArray(se === null || se === void 0 ? void 0 : se.slots))
                se.slots.forEach(pushFromSlot);
        });
    }
    if (Array.isArray(event === null || event === void 0 ? void 0 : event.direct_slots))
        event.direct_slots.forEach(pushFromSlot);
    if (Array.isArray(event === null || event === void 0 ? void 0 : event.slots))
        event.slots.forEach(pushFromSlot);
    return out;
};
const normalizeEventForPricing = (event) => {
    if (!event)
        return event;
    // Already details-schema — nothing to do.
    if (event.direct_slots || event.sub_events)
        return event;
    // Not list-schema either — return as-is so `getPriceRange` can handle it.
    if (!Array.isArray(event.slots))
        return event;
    return {
        ...event,
        has_sub_events: false,
        direct_slots: event.slots.map((slot) => ({
            enabled: slot.enabled !== false,
            has_pass_groups: false,
            // Preserve start/end time so `getPriceRange` can skip past slots.
            start_time: slot.start_time,
            end_time: slot.end_time,
            direct_passes: (slot.pass_details || slot.passes || []).map((p) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
                // pass_details uses `booking_price` (singular) with `cover_price` /
                // `entry_price` / `discount_*_price`. Map those into the canonical
                // `booking_prices` (plural) shape.
                const bp = (_b = (_a = p.booking_price) !== null && _a !== void 0 ? _a : p.booking_prices) !== null && _b !== void 0 ? _b : {};
                return {
                    ...p,
                    status: (_c = p.status) !== null && _c !== void 0 ? _c : "ACTIVE",
                    is_walkin_only: !!p.is_walkin_only,
                    booking_prices: {
                        cover: (_e = (_d = bp.cover_price) !== null && _d !== void 0 ? _d : bp.cover) !== null && _e !== void 0 ? _e : 0,
                        cover_discount: (_g = (_f = bp.discount_cover_price) !== null && _f !== void 0 ? _f : bp.cover_discount) !== null && _g !== void 0 ? _g : 0,
                        entry: (_j = (_h = bp.entry_price) !== null && _h !== void 0 ? _h : bp.entry) !== null && _j !== void 0 ? _j : 0,
                        entry_discount: (_l = (_k = bp.discount_entry_price) !== null && _k !== void 0 ? _k : bp.entry_discount) !== null && _l !== void 0 ? _l : 0,
                    },
                };
            }),
        })),
    };
};
const computeMaxDiscountPercent = (passes) => {
    let max = 0;
    passes.forEach((p) => {
        const prices = p.is_walkin_only ? p.walkin_prices : p.booking_prices;
        if (!prices)
            return;
        const { cover = 0, cover_discount = 0, entry = 0, entry_discount = 0, } = prices;
        if (cover > 0 && cover_discount > 0 && cover_discount <= cover) {
            max = Math.max(max, (cover_discount / cover) * 100);
        }
        if (entry > 0 && entry_discount > 0 && entry_discount <= entry) {
            max = Math.max(max, (entry_discount / entry) * 100);
        }
    });
    return max;
};
const parseDateArray = (arr) => {
    if (!arr || arr.length < 3)
        return null;
    return new Date(`${arr[0]}/${arr[1]}/${arr[2]}${arr.length >= 5 ? ` ${arr[3]}:${arr[4]}` : ""}`);
};
const formatTimeShort = (d) => {
    const minutes = d.getMinutes();
    const fmt = minutes === 0 ? "ha" : "h:mma";
    return format(d, fmt).toUpperCase();
};
const EventReservationCard = ({ event, pubColors, pubId, width, }) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const navigate = useNavigate();
    const colors = {
        primary: (_a = pubColors === null || pubColors === void 0 ? void 0 : pubColors.primary) !== null && _a !== void 0 ? _a : DEFAULTS.primary,
        secondary: (_b = pubColors === null || pubColors === void 0 ? void 0 : pubColors.secondary) !== null && _b !== void 0 ? _b : DEFAULTS.secondary,
        bg: (_c = pubColors === null || pubColors === void 0 ? void 0 : pubColors.bg) !== null && _c !== void 0 ? _c : DEFAULTS.bg,
        card_bg: (_e = (_d = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_bg) !== null && _d !== void 0 ? _d : pubColors === null || pubColors === void 0 ? void 0 : pubColors.bg) !== null && _e !== void 0 ? _e : DEFAULTS.card_bg,
        text: (_f = pubColors === null || pubColors === void 0 ? void 0 : pubColors.text) !== null && _f !== void 0 ? _f : DEFAULTS.text,
        card_border: (_g = pubColors === null || pubColors === void 0 ? void 0 : pubColors.card_border) !== null && _g !== void 0 ? _g : DEFAULTS.card_border,
    };
    // const colors = {
    //   primary: pubColors?.primary ?? DEFAULTS.primary,
    //   secondary: pubColors?.secondary ?? DEFAULTS.secondary,
    //   bg: "#111111",
    //   card_bg: "#16171C",
    //   text: "white",
    //   card_border: "#282333",
    // };
    const date = parseDateArray(event === null || event === void 0 ? void 0 : event.event_date);
    const startDate = parseDateArray(event === null || event === void 0 ? void 0 : event.event_st);
    const endDate = parseDateArray(event === null || event === void 0 ? void 0 : event.event_et);
    // Normalize once — handles list-schema (`slots[].pass_details[]`) from
    // `usePub`/`usePubEvents` and details-schema (`direct_slots[].direct_passes[]`)
    // from the single-event endpoint. Downstream code (`extractPasses`,
    // `computeMaxDiscountPercent`, `getPriceRange`) all read the details shape.
    const normalizedEvent = normalizeEventForPricing(event);
    const passes = extractPasses(normalizedEvent);
    const discountPercent = computeMaxDiscountPercent(passes);
    const { priceText, isSinglePass } = getPriceRange(normalizedEvent);
    const dateLineParts = [];
    if (date) {
        dateLineParts.push(`${format(date, "EEE").toUpperCase()}, ${format(date, "d MMM").toUpperCase()}`);
    }
    if (event === null || event === void 0 ? void 0 : event.time) {
        dateLineParts.push(event.time);
    }
    else if (startDate && endDate) {
        dateLineParts.push(`${formatTimeShort(startDate)} - ${formatTimeShort(endDate)}`);
    }
    if (priceText) {
        // For multi-pass events `priceText` already includes "onwards"; for a
        // single pass we prefix with "Priced at" to mirror EventDetails2's copy.
        dateLineParts.push(isSinglePass ? `Priced at ${priceText}` : priceText);
    }
    const handleViewDetails = () => {
        if (!(event === null || event === void 0 ? void 0 : event.uuid))
            return;
        if (startDate && pubId) {
            navigate(`/pub/detail/events/${pubId}?date=${format(startDate, "yyyy-MM-dd")}&id=${event.uuid}`);
            return;
        }
        navigate(`/event/detail/${event.uuid}`);
    };
    return (_jsxs(Box, { bg: colors.bg, borderRadius: "24px", overflow: "hidden", w: width || "100%", border: "1px solid #565656", flexShrink: 0, display: "flex", flexDir: "column", onClick: handleViewDetails, cursor: "pointer", role: "group", children: [_jsxs(Box, { position: "relative", w: "100%", maxH: "min(400px, calc(100svh - 360px))", minH: (event === null || event === void 0 ? void 0 : event.image) ? undefined : "200px", overflow: "hidden", bg: "black", borderTopRadius: "20px", display: "flex", alignItems: "center", justifyContent: "center", children: [(event === null || event === void 0 ? void 0 : event.image) && (_jsxs(_Fragment, { children: [_jsx(Image, { src: event.image, alt: "", position: "absolute", inset: 0, w: "100%", h: "100%", objectFit: "cover", filter: "blur(8px)", transform: "scale(1.15)", opacity: 0.85 }), _jsx(Image, { src: event.image, alt: (event === null || event === void 0 ? void 0 : event.event_name) || "Event", position: "relative", display: "block", w: "100%", h: "auto", maxH: "min(400px, calc(100svh - 360px))", objectFit: "contain", zIndex: 1 })] })), (event === null || event === void 0 ? void 0 : event.event_type) && (_jsx(Box, { position: "absolute", top: 3, left: 3, bg: "rgba(20, 20, 20, 0.35)", color: "white", px: 3, py: 1.5, borderRadius: "full", fontSize: "xs", fontWeight: "700", letterSpacing: "0.05em", textTransform: "uppercase", zIndex: 2, boxShadow: "0px 8px 16px 0px #00000014, -2.11px -2.11px 2.11px -2.11px #FFFFFF inset, 2.11px 2.11px 2.11px -2.11px #FFFFFF inset", css: {
                            backdropFilter: "blur(31.578947067260742px)",
                            WebkitBackdropFilter: "blur(31.578947067260742px)",
                        }, children: event.event_type }))] }), _jsxs(Flex, { flexDir: "column", gap: 1, pt: [3, 3, 4], pb: discountPercent > 0 ? [1, 1, 2] : [3, 3, 4], children: [_jsx(Text, { fontSize: ["lg", "lg", "xl"], fontWeight: "700", color: colors.text, textTransform: "uppercase", px: 4, noOfLines: 1, children: event === null || event === void 0 ? void 0 : event.event_name }), dateLineParts.length > 0 && (_jsx(Text, { fontSize: ["11px", "xs"], fontWeight: "500", color: colors.text, px: 4, children: dateLineParts.join("  |  ") })), discountPercent > 0 && (_jsxs(Box, { children: [_jsx(Box, { w: "calc(100% - 2rem)", borderTop: `1px dashed ${colors.text}`, opacity: 0.3, my: 1, mx: "auto" }), _jsx(EventOfferTicket, { discountPercent: discountPercent, bgColor: colors.card_bg, borderColor: colors.primary, overlayBorderColor: colors.card_border, dashColor: colors.primary, textColor: colors.primary, overlayColor: colors.bg + "33", stubStrokeColor: colors.bg + "33", onClick: (e) => {
                                    e.stopPropagation();
                                    handleViewDetails();
                                } })] }))] }), _jsx(Button, { w: "100%", bg: colors.primary, color: "white", borderTopRadius: 0, borderBottomRadius: "20px", py: 6, fontSize: "sm", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.05em", _hover: { bg: colors.primary, opacity: 0.9 }, _active: { bg: colors.primary }, onClick: (e) => {
                    e.stopPropagation();
                    handleViewDetails();
                }, children: "View Details" })] }));
};
export default EventReservationCard;
